import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, HostBinding, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CrmConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { hasTarget } from '@frontend/sports/common/core/utils/dom';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { ActiveModal } from '@frontend/sports/modal/feature';
import { DeviceService, WINDOW } from '@frontend/vanilla/core';
import { Store, createSelector } from '@ngrx/store';
import { AccaBoostSitecoreData } from 'packages/sports/web/app/src/acca-boost-base/acca-boost-data.model';
import { AccaBoostTokenLoaderService } from 'packages/sports/web/app/src/acca-boost-base/acca-boost-token-loader.service';
import { Sport } from 'packages/sports/web/app/src/tokens-base/token-base.models';
import { Observable, combineLatest, filter, finalize, map, of, switchMap } from 'rxjs';

import { IBetslipRootState } from '../../../../base/store/state';
import { quickBetStateSelector } from '../../../quick-bet/quick-bet.selectors';
import { RewardTokensActions } from '../../actions';
import { AccaBoostLadderItem, AccaBoostToken, AvailableTokensType, RewardTokenContext } from '../../reward-tokens.model';
import { selectRewardTokens, selectTokensEligibilityState } from '../../selectors';
import { getEligibilityStateForContext, getEligibleAccaBoost } from '../../services/linear-reward-tokens.utils';
import { getTokensType, isAccaBoostToken } from '../../services/reward-tokens.utils';
import { IRewardTokenEligibilityState, TokensRecord } from '../../state';

export enum RewardTokenInfoType {
    AccaBoost = '1',
    Rewards = '2',
}

export interface RewardTokensInfoViewModel {
    rewardTokenMessage?: string;
    rewardTokenTitle?: string;
    accaBoostContent?: AccaBoostContentViewModel;
    coverQuickBet?: boolean;
}

export interface AccaBoostContentViewModel {
    title: string;
    descriptionTitle: string;
    descriptionText: string;
    ladderTitle: string;
    sportsTitle: string;
    sportsText: string;
    ladder: AccaBoostLadderItem[];
    maxBoostTitle: string;
    maxBoostValue: number;
    minOdds: number;
    sports: Sport[];
    accaBoostMaxBoost: string;
    accaBoostMinOdds: string;
    accaBoostSelections: string;
    accaBoostShowLess: string;
    accaBoostShowMore: string;
    accaBoostWhatSports: string;
    accaBoostWhatYouCanGet: string;
    accaBoostDescription: string;
}

export const selectRewardTokensInfoState = createSelector(selectTokensEligibilityState, selectRewardTokens, (eligibilityState, tokens) => ({
    eligibilityState,
    tokens,
}));

@Component({
    selector: 'bs-reward-tokens-info',
    templateUrl: './reward-tokens-info.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./reward-tokens-info.component.scss'],
    standalone: false,
})
export class RewardTokensInfoComponent implements OnInit {
    @Input() inOverlay = false;
    @Input() modelDialog = false;
    @Input() tokenContext: RewardTokenContext;

    rewardTokenInfoType = RewardTokenInfoType;
    viewState$: Observable<RewardTokensInfoViewModel | null>;
    accaBoostSelected?: boolean;
    isRewardDrawerEnabled = false;
    showAccaBoost = false;
    isOverlayBackGround = false;
    @Input() showDesktopAccaBoostInfo: boolean;
    show = false;
    private readonly destroyRef = inject(DestroyRef);
    private readonly _window = inject(WINDOW);

    constructor(
        private elementRef: ElementRef,
        public sitecore: Sitecore,
        private tokensConfig: CrmConfig,
        private store: Store<IBetslipRootState>,
        private tokenLoaderService: AccaBoostTokenLoaderService,
        private deviceService: DeviceService,
        private activeModal: ActiveModal,
    ) {}

    @HostBinding('class') className = 'reward-tokens-module';

    @HostBinding('class.reward-drawer__list') get rewardDrawerEnabled(): boolean {
        return this.isRewardDrawerEnabled;
    }

    @HostBinding('class.acca-boost-ladder-info') get isAccaboostLadderInfo(): boolean {
        return this.showDesktopAccaBoostInfo && !this.deviceService.isMobile;
    }

    @HostBinding('class.reward-drawer__list--mobile') get ismobile(): boolean {
        return this.deviceService.isMobile;
    }

    ngOnInit(): void {
        const tokensInfoState$ = this.store.select(selectRewardTokensInfoState);

        this.isRewardDrawerEnabled = this.tokensConfig.tokens.isRewardDrawerEnabled;
        this.viewState$ = combineLatest([this.store.select(quickBetStateSelector), tokensInfoState$]).pipe(
            filter(([quickBetState, _]) => quickBetState.inOverlay === this.inOverlay || this.modelDialog),
            switchMap(([_, tokenInfoState]) => {
                if (this.modelDialog) {
                    return this.getAccaBoostContent$(tokenInfoState.eligibilityState, tokenInfoState.tokens).pipe(
                        map((accaBoostData) => this.getViewModel(tokenInfoState.eligibilityState, tokenInfoState.tokens, accaBoostData)),
                    );
                }

                return of(null);
            }),
        );

        this.viewState$
            .pipe(
                finalize(() => {
                    this.removeClickListener();
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((viewState) => {
                if (viewState) {
                    this.addClickListener();
                } else {
                    this.removeClickListener();
                }
            });
    }

    tabSelected = (id: string) => {
        const isAccaBoost = id === RewardTokenInfoType.AccaBoost;
        this.handleClick(isAccaBoost);
    };

    tabClick(event: Event, isAccaBoost: boolean) {
        event.preventDefault();
        event.stopPropagation();
        this.handleClick(isAccaBoost);
    }

    showMore = (event: Event) => {
        event.preventDefault();
        event.stopPropagation();
        this.show = !this.show;
    };

    closeClick = (event: Event, okButtonClicked: boolean = false) => {
        const isAccaBoostSelected = this.isRewardDrawerEnabled || this.accaBoostSelected;
        const isDesktopAccaInfo = !this.deviceService.isMobile && okButtonClicked;
        const isAccaBoostUsInfoClicked = this.showDesktopAccaBoostInfo || (isAccaBoostSelected && isDesktopAccaInfo);

        if (isAccaBoostUsInfoClicked) {
            this.activeModal.close();
        } else {
            const linkClicked = this.elementRef.nativeElement.contains(event.target) && hasTarget(event, HTMLAnchorElement);
            this.store.dispatch(RewardTokensActions.hideOverlayInfo({ okButtonClicked, linkClicked }));
            this.accaBoostSelected = false;
        }
    };

    getActiveTab(): string {
        return this.accaBoostSelected ? RewardTokenInfoType.AccaBoost : RewardTokenInfoType.Rewards;
    }

    getThemeInverseIndicator() {
        return {
            'premium-dark': { isInverse: false },
            'sports-interaction': { isInverse: false },
            'oddset': { isInverse: this.accaBoostSelected ? false : true },
        };
    }

    private handleClick(isAccaBoost: boolean) {
        this.store.dispatch(RewardTokensActions.overlayTabClicked({ payload: { isAccaBoost } }));
        this.accaBoostSelected = isAccaBoost;
    }

    private getViewModel(
        eligibilityState: IRewardTokenEligibilityState,
        tokens: TokensRecord,
        accaBoostData: AccaBoostContentViewModel | undefined,
    ): RewardTokensInfoViewModel {
        const info = this.sitecore.crmTokens.info;
        if (!info) {
            return { coverQuickBet: false };
        }
        const eligibleTokens = getEligibilityStateForContext(this.tokenContext, eligibilityState)
            .map((eligibility) => tokens[eligibility.tokenId])
            .filter((t) => !isAccaBoostToken(t));

        const rewardTokenType = this.isRewardDrawerEnabled
            ? AvailableTokensType.MultipleRewards
            : eligibleTokens.length
              ? getTokensType(eligibleTokens)
              : undefined;
        const icons = this.tokensConfig.tokens.icons;

        this.accaBoostSelected = !!accaBoostData;
        const viewState: RewardTokensInfoViewModel = {
            accaBoostContent: accaBoostData,
            coverQuickBet: false,
        };

        switch (rewardTokenType) {
            case AvailableTokensType.FreeBet:
                viewState.rewardTokenMessage = interpolateDoubleBracesString(info.freeBetText, {
                    freeBetIcon: icons.freeBet ?? '',
                });
                viewState.rewardTokenTitle = info.freeBetTitle;
                break;
            case AvailableTokensType.OddsBoost:
                viewState.rewardTokenMessage = interpolateDoubleBracesString(info.oddsBoostText, {
                    oddsBoostIcon: icons.oddsBoost ?? '',
                });
                viewState.rewardTokenTitle = info.oddsBoostTitle;
                break;

            case AvailableTokensType.RiskFreeBet:
                viewState.rewardTokenMessage = interpolateDoubleBracesString(info.riskFreeText, {
                    riskFreeIcon: icons.riskFree,
                });
                viewState.rewardTokenTitle = info.riskFreeTitle;
                break;

            case AvailableTokensType.BetAndGet:
                viewState.rewardTokenMessage = interpolateDoubleBracesString(info.betAndGetText, {
                    betAndGetIcon: icons.betAndGet ?? '',
                });
                viewState.rewardTokenTitle = info.betAndGetTitle;
                break;

            case AvailableTokensType.MultipleRewards:
                viewState.rewardTokenMessage = interpolateDoubleBracesString(
                    this.isRewardDrawerEnabled ? info.multipleRewardsInfoText : info.multipleRewardsText,
                    {
                        freeBetIcon: icons.freeBet ?? '',
                        oddsBoostIcon: icons.oddsBoost ?? '',
                        riskFreeIcon: icons.riskFree,
                        betAndGetIcon: icons.betAndGet ?? '',
                        showOrHideBetAndGet: !this.tokensConfig.tokens.isBetAndGetEnabled ? 'hide-bet-and-get' : '',
                    },
                );
                viewState.rewardTokenTitle = info.multipleRewardsTitle;
                viewState.coverQuickBet = true;
                break;
            default:
                this.accaBoostSelected = true;
        }

        return viewState;
    }

    private addClickListener(): void {
        this._window.addEventListener('click', this.closeClick);
    }

    private removeClickListener(): void {
        this._window.removeEventListener('click', this.closeClick);
    }

    private getAccaBoostContent$(
        eligibilityState: IRewardTokenEligibilityState,
        tokens: TokensRecord,
    ): Observable<AccaBoostContentViewModel | undefined> {
        const token = getEligibleAccaBoost(eligibilityState, tokens);

        return token
            ? this.tokenLoaderService.loadTokenData(token.cmsItemId).pipe(
                  map((data) => {
                      if (!data) {
                          return;
                      }

                      return this.mapConfig(data, token);
                  }),
              )
            : of(undefined);
    }

    private mapConfig(data: AccaBoostSitecoreData, token: AccaBoostToken): AccaBoostContentViewModel {
        return {
            ladderTitle: this.sitecore.accaBoost.LadderTitle,
            sportsTitle: this.sitecore.accaBoost.SportsAndMarketsTitle,
            ladder: token.boostLadder,
            maxBoostTitle: this.sitecore.accaBoost.MaxBoostMinOddsTitle,
            maxBoostValue: token.maxReward,
            title: this.sitecore.accaBoost.AccaBoost,
            sportsText: data.sportsAndMarketsText,
            descriptionTitle: data.descriptionTitle,
            descriptionText: data.descriptionText,
            minOdds: token.filter.minOddsPerLeg.european,
            sports: token.filter.sports,
            accaBoostMinOdds: this.sitecore.accaBoost.AccaBoostMinOdds,
            accaBoostMaxBoost: this.sitecore.accaBoost.AccaBoostMaxBoost,
            accaBoostSelections: this.sitecore.accaBoost.Picks,
            accaBoostShowLess: this.sitecore.accaBoost.AccaBoostShowLess,
            accaBoostShowMore: this.sitecore.accaBoost.AccaBoostShowMore,
            accaBoostWhatSports: this.sitecore.accaBoost.AccaBoostWhatSports,
            accaBoostWhatYouCanGet: this.sitecore.accaBoost.AccaBoostWhatYouCanGet,
            accaBoostDescription: this.sitecore.accaBoost.AccaBoostDescription,
        };
    }
}
